import { useRecoilValue } from 'recoil';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    replanningRunOutputsState,
    replanningRunSummaryState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { useRunScans } from './useRunScans';

export interface ReviewDatasets {
    // The planning scan's structures and the dose planned on them.
    readonly sourceSet?: StructureSet;
    readonly priorDose?: DoseMatrix;
    // The target scan's structures from this run, and the dose the run returned.
    readonly propagatedSet?: StructureSet;
    readonly runDose?: DoseMatrix;
}

const onScan = (set: StructureSet | undefined, scan: Image3D | undefined) =>
    set && scan && set.image?.seriesUid === scan.seriesUid ? set : undefined;

// What the run was made from and produced, on the scans it was made between — never what is displayed.
export function useReviewDatasets(): ReviewDatasets {
    const patient = useSelectedPatient();
    // Re-rendered per changeset: doses and sets are added and removed in place.
    useDomainVersion();
    const { sourceUid, propagatedUid, propagatedDoseUid } = useRecoilValue(
        replanningRunOutputsState
    );
    const summary = useRecoilValue(replanningRunSummaryState);
    const { planning, target } = useRunScans();

    const byUid = (uid: string | undefined) =>
        uid
            ? patient?.structureSets.find((set) => set.dicomUid === uid)
            : undefined;
    const sourceSet = onScan(byUid(sourceUid), planning);
    const propagatedSet = onScan(byUid(propagatedUid), target);
    const planned = (patient?.doses ?? []).filter(
        (dose) => !isDerivedDose(dose)
    );
    const priorDoseUid = summary?.priorDoseSopInstanceUid;
    const candidate =
        (priorDoseUid
            ? planned.find((dose) => dose.sopInstanceUid === priorDoseUid)
            : undefined) ??
        planned.find((dose) => !!sourceSet && dose.structureSet === sourceSet);
    // The prior dose must be the one on the planning structures compared against it.
    const priorDose =
        candidate && sourceSet && candidate.structureSet === sourceSet
            ? candidate
            : undefined;
    const runDose = propagatedDoseUid
        ? planned.find((dose) => dose.dicomUid === propagatedDoseUid)
        : undefined;
    return { sourceSet, priorDose, propagatedSet, runDose };
}
